<template>
  <div class="heatmap_scroll" @mouseleave="currentAttentionData = null">
    <svg v-if="svgPath" class="attention_scroll" :width="containerWidth" :height="containerHeight">
      <path :d="svgPath"
        stroke-width="2"
        stroke="#fff"
        fill="rgba(20, 20, 20, 0.7)"></path>
    </svg>
    <canvas class="scroll__canvas" ref="canvas"
      @mousemove="onCanvasMouseMove"></canvas>
    <div
      class="movable__marker"
      v-if="currentAttentionData"
      :style="{ top: currentAttentionData.top + 'px' }">
      <div
        v-html="$t('heatmap.scrollAttention',
        { attention: currentAttentionData.attention, percentage: currentAttentionData.percentage })">
      </div>
    </div>
    <div
      v-for="(item, index) in reachItems"
      :key="index"
      class="fixed__marker"
      :style="{ top: item.reach + 'px' }">
      <div class="percentage">{{ item.percentage }}%</div>
    </div>
  </div>
</template>

<script>
import { getPercentage } from '@/libs/utils'

export default {
  name: 'HeatmapScroll',
  props: [
    'scrollData',
    'scale',
    'fitMode',
    'containerWidth',
    'containerHeight'
  ],
  data () {
    return {
      isLoadedOnce: false,
      reachItems: [],
      attentionItems: [],
      currentAttentionData: null
    }
  },
  computed: {
    svgPath () {
      if (!this.attentionItems.length) {
        return null
      }

      let pathString = 'M 0 0 L '
      pathString += this.attentionItems.map(item => {
        return `${item.dx} ${item.dy}`
      }).join(' L ')
      pathString += ' H 0 Z'
      return pathString
    },
    attentionSet () {
      const data = {}

      for (const item of this.attentionItems) {
        data[item.topPercentage] = item
      }
      return data
    }
  },
  watch: {
    scale (value) {
      this.clearHeatmap()
      this.loadHeatmap()
    }
  },
  mounted () {
    this.canvas = this.$refs.canvas

    if (!this.isLoadedOnce) {
      this.loadHeatmap()
    }
  },
  destroyed () {
    this.clearHeatmap()
  },
  methods: {
    clearHeatmap () {
      const ctx = this.canvas.getContext('2d')
      ctx.clearRect(0, 0, this.containerWidth, this.containerHeight)
    },
    loadHeatmap () {
      this.isLoadedOnce = true
      this.renderCanvas(this.scrollData.h20, this.scrollData.h40, this.scrollData.h60,
        this.scrollData.h80)
      this.setReachItems()
      this.setAttentionItems()
    },
    setReachItems () {
      this.reachItems = this.scrollData.reachItems.map(item => {
        return {
          reach: item.reach * this.containerHeight,
          percentage: item.percentage
        }
      })
    },
    setAttentionItems (attentionData, attentionMaxTime, scrollData, scrollKeys) {
      const aHeight = this.containerHeight / 100
      const padding = this.fitMode === 'width' ? 20 : 0

      this.attentionItems = this.scrollData.attentionItems.map((item, i) => {
        return {
          dx: item.dx * (this.containerWidth - padding),
          dy: item.dy * aHeight,
          topPercentage: item.topPercentage,
          percentage: Math.round(item.percentage),
          attention: item.attention
        }
      })
    },
    renderCanvas (h20, h40, h60, h80) {
      this.canvas.setAttribute('width', this.containerWidth)
      this.canvas.setAttribute('height', this.containerHeight)
      const ctx = this.canvas.getContext('2d')
      ctx.globalAlpha = 1
      const opacity = 0.5
      const gradient = ctx.createLinearGradient(0, 0, 0, this.containerHeight)
      gradient.addColorStop(0, `rgba(255, 0, 0, ${opacity})`)
      gradient.addColorStop(h80, `rgba(255, 255, 0, ${opacity})`)
      gradient.addColorStop(h60, `rgba(0, 255, 0, ${opacity})`)
      gradient.addColorStop(h40, `rgba(0, 255, 255, ${opacity})`)
      gradient.addColorStop(h20, `rgba(0, 0, 255, ${opacity})`)
      gradient.addColorStop(1, `rgba(0, 0, 0, ${opacity})`)
      ctx.fillStyle = gradient
      ctx.fillRect(0, 0, this.containerWidth, this.containerHeight)
    },
    onCanvasMouseMove (event) {
      const topPosition = event.offsetY
      const topPercentage = Math.ceil(getPercentage(topPosition, this.containerHeight))
      const data = this.attentionSet[topPercentage]

      if (!data) {
        return
      }

      this.currentAttentionData = {
        top: topPosition,
        percentage: Math.round(data.percentage),
        attention: data.attention
      }
    }
  }
}
</script>

<style lang="scss">
.heatmap_scroll {
  height: 100%;
  position: relative;

  .scroll__canvas {
    position: relative;
  }

  .fixed__marker {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    border-bottom: 1px solid #fff;
    z-index: 200;
    box-shadow: 0 0 3px 0 rgba(45, 45, 45, 0.3);
    pointer-events: none;
    margin-right: 47px;
  }

  .fixed__marker .percentage {
    color: #fff;
    top: -10px;
    right: -6px;
    line-height: 18px;
    position: absolute;
    z-index: 200;
    font-size: 11px;
    background-image: url(../../assets/img/heatmap/heatmap_scroll_fixed_maker.svg);
    background-repeat: no-repeat;
    font-weight: bold;
    width: 66px;
    height: 39px;
    background-position: 10px -5px;
    transform: translate(41px);
    box-sizing: border-box;
    padding-right: 6px;
    text-align: right;
  }

  .movable__marker {
    position: absolute;
    width: 100%;
    left: 0;
    top: 0;
    right: 0;
    border-bottom: 1px solid #000;
    z-index: 210;
    box-shadow: 0 2px 3px 0 rgba(45, 45, 45, 0.3);

    & > div {
      display: inline-block;
      color: #fff;
      top: -17px;
      left: 50%;
      transform: translateX(-50%);
      position: absolute;
      padding: 7px 90px;
      text-align: center;
      font-size: 13px;
      border-radius: 14.5px;
      background: rgba(0,0,0,0.7);
      line-height: 1.5;
      white-space: nowrap;
    }

    & > span {
      display: inline-block;
      position: absolute;
      top: 0;
      border-bottom: 1px solid blue;
      width: 50px;
      height:10px;

      &.border-left {
        left: 0px;
      }
      &.border-right {
        right: 0px;
      }
    }

  }

  .attention_scroll {
    position: absolute;
    z-index: 0;
    width: 100%;
    top: 0;
    left: 0;
  }
}
</style>
